/**
 * civilianLoad.js - 2026-10-21 (R2-08, Dr. Tatar; rebuilt after the MEDROE research, docs/research/RESEARCH_MEDROE.md)
 *
 * Medical rules of ELIGIBILITY (MedROE): who may receive care at the military facilities. LLE = life, limb or eyesight emergency (the life-threatening column of the matrix;
 * the surgical cases). Each patient category of the matrix has its own stream of surgical cases per day:
 *   CIVCAS_OWN    civilians hurt by our own fire = OWN_FIRE_CIVILIAN_SHARE (25 percent) of the civilian surgical cases of the card. Care is owed (liability, GC IV Art. 16):
 *                 military share = O (the matrix cell), whatever the host hospitals do.
 *   CIVCAS_OTHER  the other civilians = 75 percent. Military share = O x D[host status].
 *   HN_PARTNERED, HN_UNPARTNERED, VIP_HVT, CONTRACTORS: fixed surgical cases per day entered in the card (not a rate per 1,000: Dr. Tatar). Military share = O x D[host status].
 *   EPW_EWIA      wounded prisoners (the card's prisoner stream x EPW_SURGICAL_SHARE): always military (GC III Art. 12, locked).
 *   COALITION_MILITARY: our own and allied casualties are the main calculation; no separate stream.
 * O = the cell of the matrix, life-threatening (LLE) column: Y 1, Y* 0.5 (conditional: commander approval, bed space), N 0.
 * D = share taken when the host system is the alternative: host hospitals functional / overwhelmed / damaged / destroyed 0.2 / 0.5 / 0.7 / 1.0 (the hospitals that exist take the rest).
 * H = most the host system can take at all: 1.0 / 0.5 / 0.3 / 0.  host share h = min(1 - m, H); unmet = 1 - m - h (stabilisation only; reported, not treated).
 * Military cases go to the Role 2 Enhanced and Role 3 first, then the Role 2 Basic; the blood they use is scaled by the same cases.
 * Status: D, H, the 25 percent and the prisoner surgical share are EDUCATED GUESS (no data found: docs/research/RESEARCH_MEDROE.md).
 */
export const CIVILIAN_LOAD_ON = true;   // false = every civilian surgical case of the card is a military case and only adds blood (as before)
export const MILITARY_SHARE_WHEN_OBLIGED = Object.freeze({ FUNCTIONAL: 0.2, OVERWHELMED: 0.5, DAMAGED: 0.7, DESTROYED: 1.0 });
export const HOST_MAX_SHARE = Object.freeze({ FUNCTIONAL: 1.0, OVERWHELMED: 0.5, DAMAGED: 0.3, DESTROYED: 0 });
export const OWN_FIRE_CIVILIAN_SHARE = 0.25;   // civilians hurt by our own fire, of all civilian surgical cases. EDUCATED GUESS (Dr. Tatar 2026-10-21)
export const EPW_SURGICAL_SHARE = 0.25;       // wounded prisoners who need surgery (as the T1 share of our own wounded). EDUCATED GUESS
export const LLE_VALUE = Object.freeze({ Y: 1, 'Y*': 0.5, N: 0 });
export const EPW_BED_MULT = 1.4;               // prisoner beds per prisoner patient (mroeConfig EPW_BED_MULT): longer stay
export const CIVILIAN_LOAD_STATUS = Object.freeze({
  status: 'EDUCATED_GUESS',
  basis: 'Structure agreed with Dr. Tatar 2026-10-21; shares 0.2 / 0.5 / 0.7 / 1.0 and host maxima 1.0 / 0.5 / 0.3 / 0 are guesses; obligation from the MedROE matrix.'
});

function shares(military, hostStatus) {
  const m = Math.min(1, Math.max(0, military));
  const host = Math.min(1 - m, HOST_MAX_SHARE[hostStatus]);
  return { military: m, host, unmet: Math.max(0, 1 - m - host) };
}

const CATEGORY_VOLUME_FIELDS = Object.freeze({ HN_PARTNERED: 'hn_partnered_cases_day', HN_UNPARTNERED: 'hn_unpartnered_cases_day', VIP_HVT: 'vip_cases_day', CONTRACTORS: 'contractor_cases_day' });

/**
 * @param {Object} c matrix (MedROE matrix of the card: {CATEGORY: {lle, nonLle}}), hostStatus, civilianSurgicalDaily (card civilian stream, force),
 *   epwDailyPatients (card prisoner stream), volumes {hn_partnered_cases_day, hn_unpartnered_cases_day, vip_cases_day, contractor_cases_day}
 * @returns {{by_category:Object, military_cases:number, unmet_cases:number, civilian:{military,host,unmet,cases}}}
 */
export function eligibilityLoad({ matrix, hostStatus, civilianSurgicalDaily = 0, civilianWardDaily = 0, epwDailyPatients = 0, volumes = {} }) {
  if (!Object.prototype.hasOwnProperty.call(MILITARY_SHARE_WHEN_OBLIGED, hostStatus)) throw new Error(`civilianLoad: unknown host system status '${hostStatus}' (valid: ${Object.keys(MILITARY_SHARE_WHEN_OBLIGED).join(', ')})`);
  const cell = (cat) => {
    const v = matrix?.[cat]?.lle;
    if (v === undefined) return 1;   // no matrix: everybody eligible
    if (!Object.prototype.hasOwnProperty.call(LLE_VALUE, v)) throw new Error(`civilianLoad: matrix cell ${cat} life-threatening value must be Y, Y* or N (got: ${v})`);
    return LLE_VALUE[v];
  };
  const D = MILITARY_SHARE_WHEN_OBLIGED[hostStatus];
  const out = {};
  const add = (cat, cases, militaryShare) => {
    const sh = CIVILIAN_LOAD_ON ? shares(militaryShare, hostStatus) : { military: 1, host: 0, unmet: 0 };
    out[cat] = { cases, military_share: sh.military, host_share: sh.host, unmet_share: sh.unmet, military_cases: cases * sh.military, unmet_cases: cases * sh.unmet };
  };
  const civ = Math.max(0, civilianSurgicalDaily);
  add('CIVCAS_OWN', civ * OWN_FIRE_CIVILIAN_SHARE, cell('CIVCAS_OWN'));
  add('CIVCAS_OTHER', civ * (1 - OWN_FIRE_CIVILIAN_SHARE), cell('CIVCAS_OTHER') * D);
  Object.entries(CATEGORY_VOLUME_FIELDS).forEach(([cat, field]) => {
    const v = volumes[field];
    if (v !== undefined && (typeof v !== 'number' || Number.isNaN(v) || v < 0)) throw new Error(`civilianLoad: ${field} must be a number >= 0 (got: ${v})`);
    add(cat, v || 0, cell(cat) * D);
  });
  const epw = Math.max(0, epwDailyPatients) * EPW_SURGICAL_SHARE;
  out.EPW_EWIA = { cases: epw, military_share: 1, host_share: 0, unmet_share: 0, military_cases: epw, unmet_cases: 0 };
  const sum = (f) => Object.values(out).reduce((a2, c) => a2 + f(c), 0);
  // Ward cases (not surgical, need a hospital bed): civilians by the urgent / routine column of the matrix and the same host factors; wounded prisoners that need no surgery
  const cellWard = (cat) => {
    const v = matrix?.[cat]?.nonLle;
    if (v === undefined) return 1;
    if (!Object.prototype.hasOwnProperty.call(LLE_VALUE, v)) throw new Error(`civilianLoad: matrix cell ${cat} urgent / routine value must be Y, Y* or N (got: ${v})`);
    return LLE_VALUE[v];
  };
  const ward = Math.max(0, civilianWardDaily);
  const wardOwn = ward * OWN_FIRE_CIVILIAN_SHARE * (CIVILIAN_LOAD_ON ? shares(cellWard('CIVCAS_OWN'), hostStatus).military : 1);
  const wardOther = ward * (1 - OWN_FIRE_CIVILIAN_SHARE) * (CIVILIAN_LOAD_ON ? shares(cellWard('CIVCAS_OTHER') * D, hostStatus).military : 1);
  const wardEpw = Math.max(0, epwDailyPatients) * (1 - EPW_SURGICAL_SHARE);
  const wardMilitary = wardOwn + wardOther + wardEpw;
  // beds taken per day by the military: surgical and ward patients; prisoners count EPW_BED_MULT beds each
  const epwBeds = (out.EPW_EWIA.military_cases + wardEpw) * EPW_BED_MULT;
  const otherBeds = sum((c) => c.military_cases) - out.EPW_EWIA.military_cases + wardOwn + wardOther;
  const civCases = out.CIVCAS_OWN.cases + out.CIVCAS_OTHER.cases;
  const civMil = out.CIVCAS_OWN.military_cases + out.CIVCAS_OTHER.military_cases;
  const civUnmet = out.CIVCAS_OWN.unmet_cases + out.CIVCAS_OTHER.unmet_cases;
  return {
    by_category: out,
    military_cases: sum((c) => c.military_cases),
    unmet_cases: sum((c) => c.unmet_cases),
    ward_military_cases: wardMilitary,
    bed_admissions: epwBeds + otherBeds,
    civilian: { cases: civCases, military: civCases > 0 ? civMil / civCases : 0, unmet: civCases > 0 ? civUnmet / civCases : 0, host: civCases > 0 ? 1 - civMil / civCases - civUnmet / civCases : 0 }
  };
}

/**
 * Split the military civilian surgical cases of a day over the Role 2 Enhanced, the Role 3 and the Role 2 Basic.
 * @param {number} cases military civilian surgical cases of the day (force)
 * @param {Array} destinations [{id, full_capacity}] in the order they take cases (Role 2 Enhanced first, then Role 3)
 * @returns {{taken: Object, to_r2b: number}}
 */
export function distributeCivilianCases(cases, destinations) {
  let left = Math.max(0, cases);
  const taken = {};
  destinations.forEach((d) => {
    const t = Math.min(left, Math.max(0, d.full_capacity));
    taken[d.id] = t;
    left -= t;
  });
  return { taken, to_r2b: left };
}
